// Slider Rocks!
import { closest } from '@jotforminc/utils';
import {
  addClass, removeClass, map, triggerEvent, forEach
} from './utils';

class Slider {
  constructor(props) {
    if (!props.container) {
      throw new Error('Container not found');
    }
    this.setRange = this.setRange.bind(this);
    this.setPointerValues = this.setPointerValues.bind(this);
    this.handleMoveStart = this.handleMoveStart.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleMoveEnd = this.handleMoveEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.setFilled = this.setFilled.bind(this);
    this.moveNextStep = this.moveNextStep.bind(this);
    this.movePreviousStep = this.movePreviousStep.bind(this);
    this.makeInitialSelection = this.makeInitialSelection.bind(this);
    this.options = this.getDefaultOptions();
    if (props.options && typeof props.options === typeof {}) {
      this.options = Object.assign(this.options, props.options);
    }
    this.props = props;
    this.container = props.container;
    this.init();
  }

  getDefaultOptions() {
    return {
      rowSelector: '.jfMatrixScale',
      labelSelector: '.jfMatrixScale-label',
      rangeSelector: '.jfMatrixScale-item',
      scaleSeperatorSelector: '.scaleSep',
      visibleClass: 'isVisible',
      transitionClass: 'hasTransition',
      filledClass: 'isFilled',
      scaleListItemSelector: '.jfMatrixInputList-item'
    };
  }

  getPixelByPercent() {
    const { sliderRow } = this;
    const { width } = sliderRow.getBoundingClientRect();
    return (width / 100);
  }

  setRange(percent, isInit) {
    const { container, selectedRangeArea, allScales } = this;
    const { visibleClass, scaleSeperatorSelector, labelSelector } = this.options;

    // update styles
    selectedRangeArea.style.width = `${percent}%`;

    // get nearest scale seperator
    const nearest = this.getNearestScale(allScales, percent);
    // find new label
    const newLabel = container.querySelector(`${labelSelector}[data-scale="${nearest}"]`);
    const newLabelScale = newLabel.getAttribute('data-scale');
    const scaleSeperator = container.querySelector(`${scaleSeperatorSelector}[data-scale="${newLabelScale}"]`);
    const input = scaleSeperator.querySelector('input');
    // do not check the slider inputs initially, if they are required. #1742342
    if (input && (!isInit || container.dataset.required === 'No')) {
      input.checked = true;
      const ID = input.getAttribute('id');
      const inputs = container.querySelectorAll('input');
      forEach(inputs, el => {
        if (el.getAttribute('id') !== ID) {
          el.checked = false; // eslint-disable-line
        }
      });

      const formLine = closest(input, '.form-line');
      if (formLine) { triggerEvent(formLine, 'change'); }
    }
    const visibleLabel = this.getVisibleLabel();
    if (visibleLabel !== newLabel) { // prevent unnecessary update
      removeClass(visibleLabel, visibleClass);
      addClass(newLabel, visibleClass);
    }
    this.currentPercentage = percent;
    const questionLabel = document.querySelector(`#label_${this.props.qid}`).innerText;
    const visibleLabelAlt = container.querySelector(`${labelSelector}[data-scale="${nearest}"] img`)?.alt;
    this.sliderButton.setAttribute('aria-valuenow', percent);
    this.sliderButton.setAttribute('aria-valuetext', percent + ' percent of ' + questionLabel + (visibleLabelAlt ? ' / ' + visibleLabelAlt : ''));
  }

  getVisibleLabel() {
    const { container } = this;
    const { labelSelector, visibleClass } = this.options;
    return container.querySelector(`${labelSelector}.${visibleClass}`);
  }

  setPointerValues(startX = 0) {
    const { selectedRangeArea } = this;
    const pixelByPercent = this.getPixelByPercent();
    this.startX = startX;
    this.positionX = startX;
    this.currentPercentage = this.currentPercentage || selectedRangeArea.offsetWidth / pixelByPercent;
  }

  isTouchEvent(event) {
    return !!(event.touches && event.touches[0]);
  }

  getNearestScale(allScales, percent) {
    return allScales.reduce((prev, curr) => {
      return (Math.abs(curr - percent) < Math.abs(prev - percent) ? curr : prev);
    });
  }

  // makes initial selections
  makeInitialSelection() {
    // make initial selection #fixes 1416490
    try {
      const initialWith = this.selectedRangeArea.style.width;
      if (initialWith) {
        const parsedWidth = parseInt(initialWith, 10);
        this.lastSelection = parsedWidth;
        this.setRange(parsedWidth, true);
        // do not add filledClass to the sliders initially, if they are required. #1742342
        if (this.container.dataset.required === 'No') {
          this.setFilled();
        }
      }
    } catch (e) {
      // console.log('initial selection not available');
    }
  }

  handleMove(event) {
    const { clientX } = this.isTouchEvent(event) ? event.touches[0] : event;
    const { positionX, currentPercentage } = this;
    // const { filledClass } = this.
    const pixelByPercent = this.getPixelByPercent();
    const currentPx = clientX - positionX;
    this.positionX = clientX;
    const currentPercent = currentPx / pixelByPercent;
    let value = currentPercentage + currentPercent;
    if (value <= 0) { value = 0; } else if (value >= 100) { value = 100; }
    this.setRange(value);
  }

  handleMoveEnd() {
    const { currentPercentage, allScales, selectedRangeArea } = this;
    const { transitionClass } = this.options;
    const nearest = parseFloat(this.getNearestScale(allScales, currentPercentage));
    selectedRangeArea.style.width = `${nearest}%`;
    if ((nearest !== this.lastSelection)) {
      // fire on change event if specified
      if (this.props.onChange && typeof this.props.onChange === 'function') {
        this.props.onChange({
          value: nearest
        });
      }
      addClass(selectedRangeArea, transitionClass);
      this.setRange(nearest);
      this.lastSelection = nearest;
    }

    // remove move event listenrs
    global.document.body.removeEventListener('mousemove', this.handleMove);
    global.document.body.removeEventListener('touchmove', this.handleMove);

    // remove end listeners
    global.document.body.removeEventListener('mouseup', this.handleMoveEnd);
    global.document.body.removeEventListener('touchend', this.handleMoveEnd);

    // Ticket ID: 1514952
    // We need to trigger click function after all events, because condition checking works in body's click event listener
    this.sliderRow.click();
  }

  setFilled() {
    const { selectedRangeArea } = this;
    const { scaleListItemSelector, filledClass } = this.options;
    const scaleListItem = closest(selectedRangeArea, scaleListItemSelector);

    addClass(scaleListItem, filledClass);
  }

  handleMoveStart(event) {
    const { selectedRangeArea, sliderRow } = this;
    const { transitionClass } = this.options;
    const { left } = sliderRow.getBoundingClientRect();
    const { clientX } = this.isTouchEvent(event) ? event.touches[0] : event;
    const startPercent = (clientX - left) / this.getPixelByPercent();
    this.setRange(startPercent);
    this.setFilled();
    removeClass(selectedRangeArea, transitionClass);
    this.setPointerValues(clientX);

    // add move event listeners
    global.document.body.addEventListener('mousemove', this.handleMove);
    global.document.body.addEventListener('touchmove', this.handleMove);

    // add end event listeners
    global.document.body.addEventListener('mouseup', this.handleMoveEnd);
    global.document.body.addEventListener('touchend', this.handleMoveEnd);

    // todo :: add cancel listeners
  }

  moveNextStep() {
    const stepSize = this.allScales[1] - this.allScales[0];
    if (this.currentPercentage + stepSize <= 100) {
      this.setRange(this.currentPercentage + stepSize);
    } else {
      this.setRange(100);
    }
  }

  movePreviousStep() {
    const stepSize = this.allScales[1] - this.allScales[0];
    if (this.currentPercentage - stepSize >= 0) {
      this.setRange(this.currentPercentage - stepSize);
    } else {
      this.setRange(0);
    }
  }

  handleKeyDown(event) {
    if (event.keyCode === 39) { // Right arrow
      event.stopPropagation();
      this.moveNextStep();
    } else if (event.keyCode === 37) { // Left arrow
      event.stopPropagation();
      this.movePreviousStep();
    }
  }

  init() {
    const { rowSelector, labelSelector, rangeSelector } = this.options;
    const { container } = this;
    this.sliderRow = container.querySelector(rowSelector);
    this.allScales = map(container.querySelectorAll(labelSelector), e => { return (e.getAttribute('data-scale')); });
    this.selectedRangeArea = container.querySelector(rangeSelector);
    this.sliderButton = this.selectedRangeArea.querySelector('button');
    setTimeout(this.makeInitialSelection, 100); // conflict with updateMatrixInputs
    // Accessibility attributes
    this.sliderButton.setAttribute('role', 'slider');
    this.sliderButton.setAttribute('aria-valuemax', this.allScales[this.allScales.length - 1]);
    this.sliderButton.setAttribute('aria-valuemin', this.allScales[0]);
    // Bind start events
    this.sliderRow.addEventListener('mousedown', this.handleMoveStart);
    this.sliderRow.addEventListener('touchstart', this.handleMoveStart);
    this.sliderButton.addEventListener('keydown', this.handleKeyDown);
  }
}

export default Slider;
